"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { CompanySelectWithModal } from "@/components/ui/selects/components/CompanySelectWithModal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { useUniversalAuth, useGlobalModalStack } from "@wtc/ui";
import type { Company } from "@/lib/types/database";
import { useCompanyHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { useModalContext } from "@/lib/hooks/ui/useModalContext";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { ConsolidateCompanyModalData } from "@/lib/modals/data/modal-data-types";

interface CompanyConsolidationModalProps {
  context: any;
  id: string;
  innerProps: {
    companyId: string;
  };
}

// Helper function to convert GraphQL Companies type to Company type
function adaptCompaniesToCompany(companies: any): Company {
  return {
    ...companies,
    created_at: companies.created_at ?? "",
    updated_at: companies.updated_at ?? "",
  } as Company;
}

interface CompanyConsolidationFormProps {
  companyId?: string;
  onClose?: () => void;
}

/**
 * Company Consolidation Form - Content component
 * Used by both the wrapper and legacy modal patterns
 */
export function CompanyConsolidationForm({ companyId, onClose }: CompanyConsolidationFormProps) {
  const { appUser, session } = useUniversalAuth();
  const [targetCompanyId, setTargetCompanyId] = useState<string>("");
  const [isConsolidating, setIsConsolidating] = useState(false);

  // Fetch company data
  const companyHooks = useCompanyHooks();
  const {
    data: companyData,
    isLoading,
    refetch,
  } = companyHooks.useSingle(companyId || "", { enabled: !!companyId });
  const sourceCompany = useMemo(
    () => (companyData ? adaptCompaniesToCompany(companyData) : null),
    [companyData]
  );

  const handleConsolidate = useCallback(async () => {
    if (!targetCompanyId) {
      toast.error("Please select a target company to consolidate into");
      return;
    }

    if (!sourceCompany) {
      toast.error("Source company not loaded");
      return;
    }

    if (targetCompanyId === sourceCompany.id) {
      toast.error("Cannot consolidate a company into itself");
      return;
    }

    setIsConsolidating(true);

    try {
      const token = session?.access_token;
      if (!token) {
        toast.error("Authentication required. Please log in again.");
        return;
      }

      const response = await fetch("/api/companies/consolidate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sourceCompanyId: sourceCompany.id,
          targetCompanyId: targetCompanyId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to consolidate companies");
      }

      toast.success(
        `Successfully consolidated company: ${result.migratedContactCount} contacts migrated`
      );
      setTargetCompanyId("");
      onClose?.();
      refetch();
    } catch (error) {
      console.error("Consolidation error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to consolidate companies");
    } finally {
      setIsConsolidating(false);
    }
  }, [targetCompanyId, sourceCompany, onClose, refetch, session?.access_token]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error if no companyId
  if (!companyId) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500">
          No company selected. Please click the action from a company row.
        </p>
      </div>
    );
  }

  // Show error if no company data
  if (!sourceCompany) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500">Unable to load company data for ID: {companyId}</p>
        <p className="text-xs text-gray-400 mt-2">
          Please try again or contact support if the problem persists.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          This action will merge all data from the source company into the target company and then
          delete the source company. This cannot be undone.
        </AlertDescription>
      </Alert>

      {/* Source Company Display */}
      <div className="p-4 bg-card-50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Source Company (will be deleted):</p>
        <p className="text-lg font-semibold text-card-foreground">{sourceCompany.name}</p>
      </div>

      {/* Target Company Selection */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Target Company (keep this one):</label>
        <CompanySelectWithModal
          value={targetCompanyId}
          onValueChange={setTargetCompanyId}
          placeholder="Select target company..."
          highZIndex={true}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} disabled={isConsolidating}>
          Cancel
        </Button>
        <Button
          onClick={handleConsolidate}
          disabled={isConsolidating || !targetCompanyId}
          className="bg-red-600 hover:bg-red-700"
        >
          {isConsolidating ? (
            <>
              <GitMerge className="w-4 h-4 mr-2 animate-spin" />
              Consolidating...
            </>
          ) : (
            <>
              <GitMerge className="w-4 h-4 mr-2" />
              Consolidate
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Company Consolidation Modal - Mantine modals pattern (legacy)
 * Receives companyId via innerProps
 */
export function CompanyConsolidationModal({
  context,
  id,
  innerProps,
}: CompanyConsolidationModalProps) {
  return (
    <CompanyConsolidationForm
      companyId={innerProps?.companyId}
      onClose={() => context.closeModal(id)}
    />
  );
}

/**
 * Company Consolidation Modal Wrapper - Global Modal Stack pattern
 * Reads companyId from modal context via useModalContext hook
 */
export function CompanyConsolidationModalWrapper() {
  const { closeModal } = useGlobalModalStack();
  const modalData = useModalContext<ConsolidateCompanyModalData>("consolidate-company");
  const companyId = modalData?.companyId;

  return (
    <CompanyConsolidationForm
      companyId={companyId}
      onClose={() => closeModal("consolidate-company")}
    />
  );
}
